import { useState, useEffect } from "react";
import { Trash2, Pencil, Plus, User } from "lucide-react";
import { Button, Checkbox, Modal, Table, Select } from "../../../components";
import axios from "axios";
import { GET_ROUTES_URL, DELETE_ROUTE_URL, GET_DRIVERS_URL } from "../../../features/UrlHelper";

// Predvolená farba novej trasy. Je to hodnota, ktorá sa ukladá do DB
// (routes.color_code), nie štýl obrazovky — preto ostáva hex.
const PREDVOLENA_FARBA_TRASY = "#3b82f6"; // farba-z-dat

function RoutesSection() {
  const [routes, setRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [routeForm, setRouteForm] = useState({
    id: null,
    name: "",
    color_code: PREDVOLENA_FARBA_TRASY,
    default_driver_id: "",
    interny_vydaj: false,
  });


  useEffect(() => {
    loadRoutes();
    loadDrivers();
  }, []);

  async function loadRoutes() {
    try {
      const response = await axios.get(GET_ROUTES_URL);
      setRoutes(response.data);
    } catch (error) {
      console.error("Error fetching routes:", error);
    } finally {
      setLoadingRoutes(false);
    }
  }

  async function loadDrivers() {
    try {
      const response = await axios.get(GET_DRIVERS_URL);
      setDrivers(response.data);
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  }

  const openAddRoute = () => {
    setEditingRoute(null);
    setRouteForm({ id: null, name: "", color_code: PREDVOLENA_FARBA_TRASY, default_driver_id: "", interny_vydaj: false });
    setRouteModalOpen(true);
  };

  const openEditRoute = (route) => {
    setEditingRoute(route);
    setRouteForm({
      id: route.id,
      name: route.name || "",
      color_code: route.color_code || PREDVOLENA_FARBA_TRASY,
      default_driver_id: route.default_driver_id ? String(route.default_driver_id) : "",
      interny_vydaj: route.interny_vydaj === true,
    });
    setRouteModalOpen(true);
  };

  const handleRouteSubmit = (e) => {
    e.preventDefault();
    const data = {
      id: routeForm.id,
      name: routeForm.name,
      color_code: routeForm.color_code,
      default_driver_id: routeForm.interny_vydaj ? null : (routeForm.default_driver_id || null),
      interny_vydaj: routeForm.interny_vydaj === true,
    };

    if (editingRoute) {
      axios
        .post(GET_ROUTES_URL, data)
        .then(() => loadRoutes())
        .catch((error) => console.error("Failed to update route:", error));
    } else {
      axios
        .put(GET_ROUTES_URL, data)
        .then(() => loadRoutes())
        .catch((error) => console.error("Failed to create route:", error));
    }

    setRouteModalOpen(false);
  };

  const deleteRoute = (id) => {
    axios
      .delete(DELETE_ROUTE_URL(id))
      .then(() => {
        setRoutes((prev) => prev.filter((r) => r.id !== id));
      })
      .catch((error) => console.error("Failed to delete route:", error));
    setDeleteConfirm(null);
  };

  const driverLabel = (route) => {
    if (!route.driver_name && !route.driver_surname) return "-";
    return `${route.driver_name || ""} ${route.driver_surname || ""}`.trim();
  };

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Správa trás</h2>
        <Button variant="primary" onClick={openAddRoute}>
          <Plus size={16} /> Pridať trasu
        </Button>
      </div>

      <div className="admin__table-wrapper">
        <Table
          columns={[
            {
              key: "color_code",
              label: "Farba",
              sortable: false,
              render: (val) =>
                val ? (
                  <span
                    style={{
                      display: "inline-block",
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      backgroundColor: val,
                      border: "1px solid var(--c-neutral-300)",
                      verticalAlign: "middle",
                    }}
                    title={val}
                  />
                ) : (
                  "-"
                ),
            },
            {
              key: "name",
              label: "Názov trasy",
              render: (val, row) =>
                row.interny_vydaj ? (
                  <span className="admin__table-label">
                    {val} <em className="admin__badge">výdaj na prevádzke</em>
                  </span>
                ) : (
                  val
                ),
            },
            {
              key: "default_driver_id",
              label: "Predvolený vodič",
              render: (_, row) => (row.interny_vydaj ? "—" : driverLabel(row)),
            },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) => (
                <div className="admin__table-actions">
                  <button
                    className="admin__icon-btn"
                    onClick={() => openEditRoute(row)}
                    title="Upraviť"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="admin__icon-btn admin__icon-btn--danger"
                    onClick={() => setDeleteConfirm(row.id)}
                    title="Zmazať"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ),
            },
          ]}
          data={routes}
          searchPlaceholder="Hľadať trasu..."
          emptyMessage="Žiadne trasy"
        />
      </div>

      {/* Add / Edit route modal */}
      <Modal
        isOpen={routeModalOpen}
        onClose={() => setRouteModalOpen(false)}
        title={editingRoute ? "Upraviť trasu" : "Pridať trasu"}
      >
        <form className="admin__form" onSubmit={handleRouteSubmit}>
          <div className="admin__form-field">
            <label className="admin__form-label">Názov trasy</label>
            <input
              maxLength={20}
              className="admin__form-input"
              value={routeForm.name}
              onChange={(e) =>
                setRouteForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="napr. Sever"
              required
            />
          </div>

          <div className="admin__form-field">
            <label className="admin__form-label">Farebné označenie</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                type="color"
                value={routeForm.color_code || PREDVOLENA_FARBA_TRASY}
                onChange={(e) =>
                  setRouteForm((prev) => ({ ...prev, color_code: e.target.value }))
                }
                style={{
                  width: 44,
                  height: 36,
                  padding: 2,
                  border: "1px solid var(--c-neutral-300)",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: "none",
                }}
              />
              <span style={{ fontSize: 13, color: "var(--c-neutral-600)" }}>
                {routeForm.color_code || PREDVOLENA_FARBA_TRASY}
              </span>
            </div>
          </div>

          {/* Trasa vydaja na prevadzke (R53). Nie je to rozvoz: idu na nu
              objednavky, po ktore si zakaznik pride sam, takze nema vodica.
              Bez takej trasy sa webove objednavky nedaju zapnut (R57). */}
          <div className="admin__form-field">
            <Checkbox
              checked={routeForm.interny_vydaj}
              onChange={(val) =>
                setRouteForm((prev) => ({
                  ...prev,
                  interny_vydaj: val,
                  default_driver_id: val ? "" : prev.default_driver_id,
                }))
              }
              label="Výdaj na prevádzke (nie je to rozvozová trasa)"
            />
            <p className="admin__form-hint">
              Na túto trasu idú objednávky, po ktoré si zákazník príde sám. Vodičovi sa
              neprideľuje. Prvá objednávka nového zákazníka ide vždy sem, kým mu
              nepridelíte rozvozovú trasu v jeho karte.
            </p>
          </div>

          <div className="admin__form-field">
            <Select
              label="Predvolený vodič"
              placeholder="— bez vodiča —"
              disabled={routeForm.interny_vydaj}
              options={[
                { value: "", label: "— bez vodiča —" },
                ...drivers.map((d) => ({
                  value: String(d.id),
                  label: (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <User size={15} />
                      {d.surname} {d.name}
                    </span>
                  ),
                })),
              ]}
              value={routeForm.default_driver_id}
              onChange={(val) =>
                setRouteForm((prev) => ({ ...prev, default_driver_id: val }))
              }
            />
          </div>

          <div className="admin__form-actions">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setRouteModalOpen(false)}
            >
              Zrušiť
            </Button>
            <Button variant="primary" type="submit">
              {editingRoute ? "Uložiť zmeny" : "Pridať trasu"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Potvrdiť zmazanie"
      >
        <p>Naozaj chcete zmazať túto trasu? Táto akcia sa nedá vrátiť.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={() => deleteRoute(deleteConfirm)}
            style={{ backgroundColor: "var(--c-error)", borderColor: "var(--c-error)" }}
          >
            Zmazať
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default RoutesSection;
